import { useMemo } from "react";
import {
  Line, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, Area,
  ComposedChart,
} from "recharts";

interface MinuteChartProps {
  data: { tradeTime: number; close: number; volume: number }[];
  prevClose: number;
  height?: number;
}

const X_LABELS = [
  "09:30", "10:00", "10:30", "11:00", "11:30/13:00",
  "13:30", "14:00", "14:30", "15:00",
];

export default function MinuteChart({ data, prevClose, height = 340 }: MinuteChartProps) {
  const plotData = useMemo(() => {
    if (!data || data.length < 2) return [];
    const lastTs = data[data.length - 1].tradeTime;
    const lastDate = new Date(lastTs * 1000).toISOString().slice(0, 10);
    const dayData = data.filter((d) =>
      new Date(d.tradeTime * 1000).toISOString().slice(0, 10) === lastDate
    ).filter((d) => d.close > 0);
    const filtered = dayData.filter((d) => {
      const h = new Date(d.tradeTime * 1000).getHours();
      const m = new Date(d.tradeTime * 1000).getMinutes();
      return h * 60 + m < 690 || h * 60 + m >= 780;
    });
    // 把数据点映射到 0~8 的等间距 x 轴上
    return filtered.map((d, i) => ({
      x: (i / Math.max(filtered.length - 1, 1)) * 8,
      price: d.close,
      volume: d.volume || 0,
      rawTime: d.tradeTime,
    }));
  }, [data]);

  if (plotData.length < 2) {
    return (
      <div className="flex items-center justify-center h-[200px] bg-background-50 rounded-lg">
        <p className="text-xs text-foreground-400">暂无分时数据</p>
      </div>
    );
  }

  const firstPx = plotData[0].price;
  const lastPx = plotData[plotData.length - 1].price;
  const up = lastPx >= firstPx;
  const color = up ? "#e11d48" : "#10b981";
  const prices = plotData.map((d) => d.price);
  const minP = Math.min(...prices);
  const maxP = Math.max(...prices);
  const pad = (maxP - minP) * 0.15 || 0.5;

  return (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer height={height}>
        <ComposedChart data={plotData} margin={{ top: 8, right: 4, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="#f1f5f9" vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            type="number"
            domain={[0, 8]}
            ticks={[0, 1, 2, 3, 4, 5, 6, 7, 8]}
            tickFormatter={(v: number) => X_LABELS[v] || ""}
            interval={0}
            axisLine={{ stroke: "#e2e8f0" }}
            tickLine={false}
            tick={{ fontSize: 10, fill: "#94a3b8" }}
          />
          <YAxis
            domain={[minP - pad, maxP + pad]}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: "#94a3b8" }}
            tickFormatter={(v: number) => `¥${v.toFixed(2)}`}
            orientation="right"
            width={44}
          />
          <Tooltip
            contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "11px" }}
            labelFormatter={(val: number) => {
              const pt = plotData.find((d) => Math.round(d.x) === Math.round(val) || Math.abs(d.x - val) < 0.1);
              if (!pt?.rawTime) return "";
              const d = new Date(pt.rawTime * 1000);
              return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
            }}
            formatter={(value: number) => [`¥${value.toFixed(2)}`, "价格"]}
          />
          <defs>
            <linearGradient id={`g-${up ? "u" : "d"}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.15" />
              <stop offset="100%" stopColor={color} stopOpacity="0.01" />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="price" fill={`url(#g-${up ? "u" : "d"})`} stroke="none" />
          <ReferenceLine y={prevClose} stroke="#94a3b8" strokeWidth={1} strokeDasharray="4 4" />
          <Line type="monotone" dataKey="price" stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 3 }} />
          <Bar dataKey="volume" fill={color} opacity={0.12} maxBarSize={3} yAxisId="volume" />
          <YAxis yAxisId="volume" domain={[0, "auto"]} hide />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-between px-2 mt-1 text-[10px] text-foreground-400">
        <span>昨收 ¥{prevClose.toFixed(2)}</span>
        <span>最高 ¥{maxP.toFixed(2)}</span>
        <span>最低 ¥{minP.toFixed(2)}</span>
        <span style={{ color }}>
          {lastPx >= firstPx ? "+" : ""}{((lastPx / prevClose - 1) * 100).toFixed(2)}%
        </span>
      </div>
    </div>
  );
}
